import {
  NARROW_PHONE_BREAKPOINT,
  PHONE_BREAKPOINT,
  TABLET_BREAKPOINT
} from './breakpoints'

const upToNarrowPhone = `@media (max-width: ${NARROW_PHONE_BREAKPOINT - 1}px)`
const upToPhone = `@media (max-width: ${PHONE_BREAKPOINT - 1}px)`
const upToTablet = `@media (max-width: ${TABLET_BREAKPOINT - 1}px)`

/**
 * Component defaults that make the shared MUI primitives behave on small
 * screens, so individual pages do not each have to remember to do it.
 *
 * These are deliberately limited to things that are wrong at *every* call site
 * on a phone - a dialog that cannot be closed because its buttons are off
 * screen, a table that widens the document instead of scrolling itself. Layout
 * decisions that are page-specific stay in the pages.
 *
 * Note which threshold each rule uses. Almost everything stops at `upToPhone`
 * (600px), because a tablet has room for the normal desktop treatment and
 * changing it there is a regression, not a fix.
 *
 * The exception is the iOS focus-zoom guard, which runs to `upToTablet`
 * (900px): that is a browser behaviour rather than a layout choice, and an
 * iPad zooms on a sub-16px input exactly like a phone does.
 */
export const responsiveComponents = {
  /**
   * Toolpad's DashboardLayout hard-codes `minWidth: 100vw` on the app bar
   * toolbar. `100vw` ignores the scrollbar, so it is always a little wider than
   * the page it sits in and gives the whole document a horizontal scrollbar.
   * The descendant selector outranks Toolpad's `sx`.
   *
   * ONLY `minWidth` is reset. Toolpad also sets negative horizontal margins on
   * that toolbar and relies on its width staying `auto`. Constraining `width`
   * or `maxWidth` makes the browser resolve the margin against a clamped box
   * and leaves a dead strip beside the account menu. The toolbar's overhang is
   * inside a `position: fixed` app bar, so it does not extend the document's
   * scroll width the way `100vw` did.
   */
  MuiAppBar: {
    styleOverrides: {
      root: {
        '& .MuiToolbar-root': {
          minWidth: 0
        }
      }
    }
  },

  /**
   * A 320px drawer on a 360px phone leaves a 40px strip of page behind it and
   * is easy to open by accident. Cap it against the viewport instead.
   *
   * Scoped to the side anchors by their paper classes - MUI puts the anchor
   * class on the paper, not the root - so a bottom sheet still spans the
   * screen.
   */
  MuiDrawer: {
    styleOverrides: {
      root: {
        [upToTablet]: {
          // Doubled `&&` on purpose. Toolpad styles the same paper with its
          // own `sx` at equal specificity, and `sx` is serialised after theme
          // overrides - so a single `&` loses and this rule would do nothing.
          '&& .MuiDrawer-paperAnchorLeft, && .MuiDrawer-paperAnchorRight': {
            width: 'min(320px, 85vw)',
            maxWidth: '85vw'
          }
        }
      }
    }
  },

  /**
   * Dialogs go edge to edge on a phone. The default 32px margin plus
   * `maxWidth: 'sm'` leaves a cramped column, and a tall dialog pushes its
   * action buttons off screen.
   *
   * Height is deliberately left to the content: a short confirmation stays a
   * short box rather than being stretched to fill the screen, while anything
   * taller than the viewport is capped and scrolls.
   */
  MuiDialog: {
    styleOverrides: {
      paper: {
        [upToPhone]: {
          margin: 0,
          width: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          borderRadius: 0
        }
      },

      /**
       * `maxWidth` on a Dialog defaults to `'sm'`, which is resolved against
       * the theme's breakpoint values - so while `sm` was 1200 every unsized
       * dialog in the portal was capped at 1200px. Moving `sm` to 600 would
       * silently halve all of them on desktop, which has nothing to do with
       * making the app work on a phone. Keep the width these dialogs have
       * always had, and let the rule above take over on small screens.
       *
       * MUI applies `paperWidthSm` after `paper`, so this wins where both
       * match; the small-screen rule is scoped below 600px, where this one
       * does not apply.
       */
      paperWidthSm: {
        [`@media (min-width: ${PHONE_BREAKPOINT}px)`]: {
          maxWidth: 1200
        }
      }
    }
  },

  /** Long dialog bodies need to scroll on their own, not stretch the dialog. */
  MuiDialogContent: {
    styleOverrides: {
      root: {
        [upToPhone]: {
          paddingLeft: 16,
          paddingRight: 16
        }
      }
    }
  },

  /** Stack dialog actions when they no longer fit side by side. */
  MuiDialogActions: {
    styleOverrides: {
      root: {
        [upToNarrowPhone]: {
          flexWrap: 'wrap',
          gap: 8,
          '& > :not(style) ~ :not(style)': {
            marginLeft: 0
          }
        }
      }
    }
  },

  /**
   * Wide tables are the single biggest source of horizontal page scroll in the
   * portal. MUI's own defaults already scroll, but several call sites override
   * them with a fixed width, so restate the cap here.
   */
  MuiTableContainer: {
    styleOverrides: {
      root: {
        maxWidth: '100%',
        WebkitOverflowScrolling: 'touch'
      }
    }
  },

  /** Tabs that overflow should scroll rather than clip. */
  MuiTabs: {
    defaultProps: {
      variant: 'scrollable',
      scrollButtons: 'auto',
      allowScrollButtonsMobile: true
    }
  },

  /**
   * The default gutters assume a desktop page. On a phone 24px each side costs
   * an eighth of the viewport.
   */
  MuiContainer: {
    styleOverrides: {
      root: {
        [upToPhone]: {
          paddingLeft: 12,
          paddingRight: 12
        }
      }
    }
  },

  /**
   * Comfortable hit targets. The Material guideline is 48px; MUI's default
   * `medium` button is 36px tall, which is fiddly with a thumb.
   *
   * Phones only. A tablet is a touch device too, but it has the room to show
   * forms at their normal density and growing every control there would change
   * pages that were already fine.
   */
  MuiButton: {
    styleOverrides: {
      root: {
        [upToPhone]: {
          minHeight: 44
        }
      }
    }
  },

  MuiIconButton: {
    styleOverrides: {
      root: {
        [upToPhone]: {
          minWidth: 44,
          minHeight: 44
        }
      },
      sizeSmall: {
        [upToPhone]: {
          minWidth: 36,
          minHeight: 36
        }
      }
    }
  },

  /**
   * iOS Safari zooms the page whenever a focused input's font is under 16px.
   * That zoom is not undone when the field blurs, which strands the user on a
   * magnified, sideways-scrolling page. Landscape phones need this as much as
   * portrait ones, hence the 900px threshold.
   */
  MuiInputBase: {
    styleOverrides: {
      input: {
        [upToTablet]: {
          fontSize: 16
        }
      }
    }
  }
}
